import { useRef, useState } from 'react';
import { useParams } from 'quartz-routing/react-router';
import PlaylistThumbnail from '../features/savedItems/components/PlaylistThumbnail';
import { PlaylistAssignmentsMonitoringRoutes } from '../features/playlistAssignments';
import { PlaylistDetails, PlaylistPageHeader, usePlaylist } from '../features/savedItems';
import { PlaylistItems } from '../features/playlistItems';
import styled, { mediaQuery, css } from 'academy-common-lib/utils/Styling';
import { ScrollIntoView } from '../features/routing';
import useWootricSurvey from '../hooks/useWootricSurvey';
import PlaylistPermissionsWarningMessage from '../features/savedItems/components/PlaylistPermissionsWarningMessage';
import PlaylistNotFound from '../features/savedItems/components/PlaylistNotFound';
import { useSetLastClickedRecommendationItemFromUrl } from '../features/recommendations/recommendationsHooks';
import { Flex } from 'foundations-components/components/Flex';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { AC_PAGE_MAX_INLINE_SIZE } from 'academy-navigation-lib/components/ACPage/ACPage';
import { NavMarker } from 'react-rhumb';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AcademyWidthSection = styled.div.withConfig({
  displayName: "Playlist__AcademyWidthSection"
})(["width:100%;background-color:", ";display:flex;justify-content:center;padding-block:", ";padding-inline:", ";", ""], ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.space['400'], mediaQuery('notLaptop', css(["padding-block:", ";padding-inline:", ";"], ({
  theme
}) => theme.space['1100'], ({
  theme
}) => theme.space['1400'])));
const PlaylistControls = styled(AcademyWidthSection).withConfig({
  displayName: "Playlist__PlaylistControls"
})(["padding-block-start:0;padding-block-end:24px;padding-inline:16px;", " ", ""], mediaQuery('notMobile', css(["padding-block:", ";padding-inline:", ";"], ({
  theme
}) => theme.space['600'], ({
  theme
}) => theme.space['400'])), mediaQuery('notLaptop', css(["padding-block:", ";padding-inline:", ";"], ({
  theme
}) => theme.space['1000'], ({
  theme
}) => theme.space['1400'])));
const AcademyWidthSectionRecessed = styled(AcademyWidthSection).withConfig({
  displayName: "Playlist__AcademyWidthSectionRecessed"
})(["background-color:", ";"], ({
  theme
}) => theme.color['fill-surface-recessed']);
const SearchManagerContainer = styled(AcademyWidthSection).withConfig({
  displayName: "Playlist__SearchManagerContainer"
})(["padding-inline:", ";", " ", ""], ({
  theme
}) => theme.space['0'], mediaQuery('notMobile', css(["padding-inline:", ";"], ({
  theme
}) => theme.space['400'])), mediaQuery('notLaptop', css(["padding-inline:", ";"], ({
  theme
}) => theme.space['1400'])));
const PlaylistItemsContainer = styled.div.withConfig({
  displayName: "Playlist__PlaylistItemsContainer"
})(["display:flex;flex-direction:column;max-width:", "px;inline-size:100%;padding-block-end:", ";", ""], ({
  maxInlineSize
}) => maxInlineSize, ({
  theme
}) => theme.space['1200'], mediaQuery('notMobile', css(["padding-block-end:", ";"], ({
  theme
}) => theme.space['0'])));
const TwoColumnSection = styled.div.withConfig({
  displayName: "Playlist__TwoColumnSection"
})(["gap:", ";display:flex;flex-direction:column;max-inline-size:", "px;width:100%;", ""], ({
  theme
}) => theme.space['600'], ({
  maxInlineSize
}) => maxInlineSize, mediaQuery('notTablet', css(["display:grid;grid-template-columns:1fr minmax(250px,420px);align-items:start;gap:", ";>:first-child{grid-column:2;grid-row:1;}>:last-child{grid-column:1;grid-row:1;}"], ({
  theme
}) => theme.space['600'])));
const BackgroundColoredSectionContent = styled.div.withConfig({
  displayName: "Playlist__BackgroundColoredSectionContent"
})(["width:100%;max-inline-size:", "px;"], ({
  maxInlineSize
}) => maxInlineSize);
function shouldShowPlaylistAssignments(playlistItemsCount) {
  return playlistItemsCount > 0;
}
const Playlist = () => {
  useWootricSurvey('CSAT');
  const {
    playlistId
  } = useParams();
  const id = parseInt(playlistId, 10);
  const {
    playlistError,
    playlist,
    playlistLoading
  } = usePlaylist(id);
  const playlistItemsRef = useRef(null);
  const [isAddItemsModalOpen, setIsAddItemsModalOpen] = useState(false);
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: [{
      id: (playlist === null || playlist === void 0 ? void 0 : playlist.id) || 0,
      type: (playlist === null || playlist === void 0 ? void 0 : playlist.type) === 'STANDARD' ? 'PLAYLIST' : 'LEARNING_PATH'
    }],
    skip: !playlist
  });
  const playlistCardModel = cardModels === null || cardModels === void 0 ? void 0 : cardModels[0];
  const isLoading = playlistLoading || cardModelsLoading;
  const maxInlineSize = AC_PAGE_MAX_INLINE_SIZE;
  useSetLastClickedRecommendationItemFromUrl({
    id,
    type: 'PLAYLIST'
  });
  const onAddContent = () => {
    setIsAddItemsModalOpen(true);
  };
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      justifyContent: "center",
      paddingBlockStart: "1000",
      size: "medium"
    });
  }
  if (playlistError || cardModelsError || !playlist || !playlistCardModel) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "PLAYLIST_DETAILS_API_ERROR",
      children: /*#__PURE__*/_jsx(PlaylistNotFound, {})
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "PLAYLIST_DETAILS_LOADED",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      inlineSize: "100%",
      children: [/*#__PURE__*/_jsx(PlaylistPageHeader, {}), /*#__PURE__*/_jsx(PlaylistPermissionsWarningMessage, {
        playlistId: id
      }), /*#__PURE__*/_jsx(PlaylistControls, {
        children: /*#__PURE__*/_jsxs(TwoColumnSection, {
          maxInlineSize: maxInlineSize,
          children: [/*#__PURE__*/_jsx(PlaylistThumbnail, {
            onAddContent: onAddContent,
            playlist: playlistCardModel
          }), /*#__PURE__*/_jsx(PlaylistDetails, {
            playlistId: id
          })]
        })
      }), /*#__PURE__*/_jsx(AcademyWidthSectionRecessed, {
        children: /*#__PURE__*/_jsx(PlaylistItemsContainer, {
          maxInlineSize: maxInlineSize,
          ref: playlistItemsRef,
          children: /*#__PURE__*/_jsx(PlaylistItems, {
            isAddItemsModalOpen: isAddItemsModalOpen,
            playlistId: id,
            setIsAddItemsModalOpen: setIsAddItemsModalOpen
          })
        })
      }), shouldShowPlaylistAssignments((playlist === null || playlist === void 0 ? void 0 : playlist.itemsCount) || 0) ?
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(SearchManagerContainer, {
        children: /*#__PURE__*/_jsx(Flex, {
          backgroundColor: "fill-surface-default",
          inlineSize: "100%",
          justifyContent: "center",
          overflow: "hidden",
          children: /*#__PURE__*/_jsx(BackgroundColoredSectionContent, {
            "data-test-id": "playlist-assignments-section",
            maxInlineSize: maxInlineSize,
            children: /*#__PURE__*/_jsx(ScrollIntoView, {
              id: "playlist-assignments",
              children: /*#__PURE__*/_jsx(PlaylistAssignmentsMonitoringRoutes, {})
            })
          })
        })
      }) : /*#__PURE__*/_jsx(NavMarker, {
        name: "PLAYLIST_ASSIGNMENTS_EMPTY"
      })]
    })
  });
};
export default Playlist;